import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ROLES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, ShieldCheck } from "lucide-react";

const empty = { name: "", email: "", password: "", role: "Admin Project", active: true };

export default function Users() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const isOwner = user?.role === "Owner";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
      setList(u.data); setRoles(r.data);
    } catch (err) { toast.error("Gagal memuat"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, password: "", role: u.role, active: u.active }); setEditId(u.id); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.email || (!editId && !form.password)) { toast.error("Nama, email, dan kata sandi wajib diisi."); return; }
    setSaving(true);
    try {
      if (editId) { await api.put(`/users/${editId}`, { name: form.name, role: form.role, password: form.password || undefined }); toast.success("User diperbarui"); }
      else { await api.post("/users", form); toast.success("User dibuat"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (u) => {
    try { await api.patch(`/users/${u.id}/status`); toast.success("Status diperbarui"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl font-bold text-navy">Users & Roles</h1>
          <p className="text-sm text-muted-foreground">{list.length} pengguna</p></div>
        {isOwner && <Button onClick={openNew} data-testid="new-user-btn" className="bg-primary hover:bg-red-700"><Plus size={16} className="mr-1.5" /> Tambah User</Button>}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : (
        <>
          <Card className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left text-xs text-muted-foreground">
                <th className="py-2.5 px-3">Nama</th><th className="px-3">Email</th><th className="px-3">Role</th><th className="px-3">Status</th><th></th>
              </tr></thead>
              <tbody>
                {list.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-t hover:bg-slate-50">
                    <td className="py-2.5 px-3 font-medium">{u.name}</td>
                    <td className="px-3 text-xs">{u.email}</td>
                    <td className="px-3"><span className="text-xs bg-navy/5 text-navy px-2 py-0.5 rounded-full font-medium">{u.role}</span></td>
                    <td className="px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{u.active ? "Aktif" : "Nonaktif"}</span></td>
                    <td className="px-3">{isOwner && <div className="flex gap-1 items-center">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil size={14} /></Button>
                      <Switch checked={u.active} onCheckedChange={() => toggle(u)} data-testid={`toggle-user-${u.email}`} />
                    </div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mobile cards — no horizontal scroll */}
          <div className="md:hidden space-y-2">
            {list.map((u) => (
              <Card key={u.id} data-testid={`user-card-${u.email}`} className="p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{u.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{u.active ? "Aktif" : "Nonaktif"}</span>
                </div>
                <div className="text-xs text-muted-foreground break-all">{u.email}</div>
                <div className="mt-1"><span className="text-xs bg-navy/5 text-navy px-2 py-0.5 rounded-full font-medium">{u.role}</span></div>
                {isOwner && (
                  <div className="flex gap-2 items-center mt-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-mobile-${u.email}`}>Edit</Button>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">Aktif <Switch checked={u.active} onCheckedChange={() => toggle(u)} data-testid={`toggle-user-mobile-${u.email}`} /></div>
                  </div>
                )}
              </Card>
            ))}
          </div>

          <Card className="p-4">
            <h3 className="font-heading font-semibold text-navy text-sm mb-3 flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /> Definisi Role & Hak Akses</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {roles.map((r) => (
                <div key={r.name} className="p-2.5 rounded-md bg-slate-50 border">
                  <div className="font-semibold text-sm text-navy">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.description}</div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editId ? "Edit User" : "Tambah User"}</DialogTitle>
            <DialogDescription>Kelola akun pengguna dan peran.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Nama *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name" /></div>
            <div className="space-y-1"><Label>Email *</Label><Input type="email" value={form.email} disabled={!!editId} onChange={(e) => set("email", e.target.value)} data-testid="user-email" /></div>
            {!editId && <div className="space-y-1"><Label>Kata Sandi *</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password" /></div>}
            <div className="space-y-1"><Label>Role *</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select></div>
            {!editId && <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="user-active" /><Label>Aktif</Label></div>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="save-user-btn" className="bg-primary hover:bg-red-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
